// COPIED from ptc-core web/src/shell/FeedbackDialog.tsx @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: line `app: 'crm'` → `app: 'conversions'` (contract §3); Tailwind utility
// classes → this app's CSS (`fb-*` in index.css); the import paths (`ApiValidationError` now lives in
// feedbackApi.ts). Behaviour unchanged. v1.1 (2026-10-27, ptc-core #354): the Language row under
// "What we'll attach", exactly as core adds it. Conformance: src/feedback/FeedbackWidgetConformance.test.tsx.

import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Textarea,
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';
import { ApiValidationError, useCreateFeedback, type FeedbackKind } from '../../feedback/feedbackApi';
import { buildFeedbackContext, type FeedbackContext } from '../../feedback/feedbackContext';

/**
 * "Report a problem" / "Suggest an improvement" (feedback-loop spec §3). Two entries, not one form
 * with a dropdown: the kind sets the triage lane, and a dropdown left at its default mislabels
 * half the reports.
 *
 * What is sent is SHOWN before it is sent (§3.2). The SSN/EIN warning sits above the text box so
 * the server-side guard rarely has to fire; when it does, its message is shown on the field.
 */
interface FeedbackDialogProps {
  kind: FeedbackKind | null;
  onClose: () => void;
  /** Test seam; production captures the live page. */
  captureContext?: () => FeedbackContext;
}

const COPY: Record<FeedbackKind, { title: string; label: string }> = {
  problem: { title: 'shell.feedback.problemTitle', label: 'shell.feedback.problemLabel' },
  improvement: { title: 'shell.feedback.improvementTitle', label: 'shell.feedback.improvementLabel' },
};

export function FeedbackDialog({ kind, onClose, captureContext = buildFeedbackContext }: FeedbackDialogProps) {
  if (kind === null) return null;
  // Keyed on kind so switching between the two entries starts a fresh form.
  return <FeedbackForm key={kind} kind={kind} onClose={onClose} captureContext={captureContext} />;
}

function FeedbackForm({ kind, onClose, captureContext }: Required<FeedbackDialogProps> & { kind: FeedbackKind }) {
  const { t } = useTranslation();
  // Captured when the dialog OPENS: the route the reporter was on, not wherever they click Send.
  const context = useMemo(() => captureContext(), [captureContext]);
  const [text, setText] = useState('');
  const [expected, setExpected] = useState('');
  const [creditByName, setCreditByName] = useState(true); // DP-FB4: default ON
  const create = useCreateFeedback();

  const fieldErrors = create.error instanceof ApiValidationError ? create.error : null;
  const otherError = create.error && !fieldErrors;
  const reference = create.data?.reference;

  function send() {
    create.mutate({
      kind,
      text: text.trim(),
      expected: kind === 'problem' && expected.trim() ? expected.trim() : undefined,
      creditByName,
      app: 'conversions',
      context,
    });
  }

  return (
    <Dialog open modalType="modal" onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t(COPY[kind].title)}</DialogTitle>
          <DialogContent>
            {reference ? (
              <p role="status" className="fb-text-sm">
                <Trans i18nKey="shell.feedback.thanks" values={{ reference }} components={{ strong: <strong /> }} />
              </p>
            ) : (
              <div className="fb-stack">
                <p className="fb-text-sm fb-warning" role="note">{t('shell.feedback.sensitiveWarning')}</p>
                <Field
                  label={t(COPY[kind].label)}
                  required
                  validationMessage={fieldErrors?.firstFor('text')}
                >
                  <Textarea value={text} onChange={(_, d) => setText(d.value)} rows={5} maxLength={4000} />
                </Field>
                {kind === 'problem' && (
                  <Field label={t('shell.feedback.expected')} validationMessage={fieldErrors?.firstFor('expected')}>
                    <Textarea value={expected} onChange={(_, d) => setExpected(d.value)} rows={3} maxLength={2000} />
                  </Field>
                )}
                <Checkbox
                  checked={creditByName}
                  onChange={(_, d) => setCreditByName(d.checked === true)}
                  label={t('shell.feedback.credit')}
                />
                <details className="fb-details">
                  <summary>{t('shell.feedback.attachSummary')}</summary>
                  <dl className="fb-dl">
                    <dt>{t('shell.feedback.contextPage')}</dt><dd className="fb-break">{context.route}</dd>
                    <dt>{t('shell.feedback.contextVersion')}</dt><dd>{context.buildSha}</dd>
                    <dt>{t('shell.feedback.contextScreen')}</dt><dd>{context.viewport}</dd>
                    <dt>{t('shell.feedback.contextTime')}</dt><dd>{context.clientTime} ({context.tz})</dd>
                    <dt>{t('shell.feedback.contextBrowser')}</dt><dd className="fb-break">{context.userAgent}</dd>
                    <dt>{t('shell.feedback.contextLanguage')}</dt><dd>{context.uiLanguage}</dd>
                    <dt>{t('shell.feedback.contextErrors')}</dt><dd>{context.recentErrors.length}</dd>
                  </dl>
                </details>
                {otherError && (
                  <p role="alert" className="fb-text-sm fb-error">
                    {t('shell.feedback.sendFailed')}
                  </p>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {reference ? (
              <Button appearance="primary" onClick={onClose}>{t('common.close')}</Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose}>{t('common.cancel')}</Button>
                <Button appearance="primary" disabled={!text.trim() || create.isPending} onClick={send}>
                  {create.isPending ? t('shell.feedback.sending') : t('shell.feedback.send')}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
